/**
 * iter91 — Issue 5: TableScroll wrapper component.
 *
 * Wraps any wide table in a horizontally scrollable container that
 * applies a `.has-overflow` class when the inner content exceeds the
 * container width — driving the right-edge gradient hint defined in
 * App.css under `.table-scroll::after`.
 *
 * Pair this with `.hide-md` / `.hide-lg` utility classes on table
 * <th>/<td> elements to drop low-priority columns at narrow widths
 * BEFORE we resort to scrolling. Scrolling should be the last-resort
 * affordance, never the default.
 */
import { useEffect, useRef, useState } from "react";

const TableScroll = ({ children, className = "", testId }) => {
  const ref = useRef(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => {
      // Only show the gradient when there's real horizontal overflow
      // AND the user hasn't scrolled to the end.
      const overflowing = el.scrollWidth > el.clientWidth + 2;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
      setHasOverflow(overflowing && !atEnd);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [children]);

  return (
    <div
      ref={ref}
      data-testid={testId}
      className={`table-scroll${hasOverflow ? " has-overflow" : ""} ${className}`.trim()}
    >
      {children}
    </div>
  );
};

export default TableScroll;
